import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  OwnerWithUnits,
  PaginatedResponse,
  CreateOwnerPayload,
  CreateOwnerWithNewUnitsPayload,
  CreateOwnerWithNewUnitsResponse,
  CreateOwnershipPayload,
  RemoveOwnershipPayload,
  Owner,
  OwnerSortField,
  SortDirection,
} from '../types'

// =====================================================
// Simple owners query (all owners)
// =====================================================

export function useOwners() {
  return useQuery({
    queryKey: ['owners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('owners')
        .select('*')
        .order('last_name', { ascending: true })

      if (error) throw error
      return data as Owner[]
    },
  })
}

// =====================================================
// Paginated owners query
// =====================================================

interface UseOwnersPaginatedOptions {
  residenceId?: string
  unitType?: string
  searchTerm?: string
  includeFormer?: boolean
  onlyWithActiveUnits?: boolean
  page: number
  pageSize: number
  sortField: OwnerSortField
  sortDirection: SortDirection
}

export function useOwnersPaginated(options: UseOwnersPaginatedOptions) {
  return useQuery({
    queryKey: ['owners', 'paginated', options],
    queryFn: async () => {
      console.log('🔍 [useOwnersPaginated] Fetching with includeFormer:', options.includeFormer)
      const { data, error } = await supabase.rpc('get_owners_paginated', {
        p_residence_id: options.residenceId || undefined,
        p_unit_type: options.unitType || undefined,
        p_search_term: options.searchTerm || undefined,
        p_include_former: options.includeFormer ?? false,
        p_only_with_active_units: options.onlyWithActiveUnits || false,
        p_page: options.page,
        p_page_size: options.pageSize,
        p_sort_field: options.sortField,
        p_sort_direction: options.sortDirection,
      })

      if (error) throw error

      // Cast data to the expected format
      const result = data as { data: OwnerWithUnits[]; count: number } | null

      // Calculate totalPages from count and pageSize
      const totalPages = result?.count ? Math.ceil(result.count / options.pageSize) : 0

      console.log('✅ [useOwnersPaginated] Received:', result?.count, 'owners, totalPages:', totalPages)

      return {
        data: result?.data || [],
        count: result?.count || 0,
        totalPages,
      } as PaginatedResponse<OwnerWithUnits>
    },
    staleTime: 0, // Force refetch on every change
  })
}

// =====================================================
// Single owner query
// =====================================================

export function useOwner(ownerId: string | null) {
  return useQuery({
    queryKey: ['owners', ownerId],
    queryFn: async () => {
      if (!ownerId) return null

      const { data, error } = await supabase
        .from('owners')
        .select(`
          *,
          ownerships(
            id,
            unit_id,
            start_date,
            end_date,
            share_numerator,
            share_denominator,
            ownership_type,
            created_at,
            updated_at,
            units(
              id,
              unit_number,
              unit_type,
              floor,
              stairwell,
              residence_id,
              residences(
                id,
                name,
                residence_key
              )
            )
          )
        `)
        .eq('id', ownerId)
        .is('deleted_at', null)
        .single()

      if (error) throw error
      return data as OwnerWithUnits
    },
    enabled: !!ownerId,
  })
}

// =====================================================
// Create owner mutation (existing units only)
// =====================================================

export function useCreateOwner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateOwnerPayload) => {
      const { data, error } = await supabase.rpc('create_owner_with_units', {
        p_first_name: payload.firstName,
        p_last_name: payload.lastName,
        p_email: payload.email || undefined,
        p_phone: payload.phone || undefined,
        p_address: payload.address || undefined,
        p_preferred_contact: payload.preferredContact,
        p_units: payload.units || [],
      })

      if (error) throw error
      return data as string // Returns owner ID
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
  })
}

// =====================================================
// Create owner with new units mutation
// =====================================================

export function useCreateOwnerWithNewUnits() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateOwnerWithNewUnitsPayload) => {
      const { data, error } = await supabase.rpc('create_owner_with_new_units', {
        p_first_name: payload.firstName,
        p_last_name: payload.lastName,
        p_email: payload.email || undefined,
        p_phone: payload.phone || undefined,
        p_address: payload.address || undefined,
        p_preferred_contact: payload.preferredContact,
        p_existing_units: payload.existingUnits || [],
        p_new_units: payload.newUnits || [],
      })

      if (error) throw error
      return data as unknown as CreateOwnerWithNewUnitsResponse
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
      queryClient.invalidateQueries({ queryKey: ['co-owner-folders'] })
    },
  })
}

// =====================================================
// Update owner mutation
// =====================================================

export function useUpdateOwner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Owner> }) => {
      const { data, error } = await supabase
        .from('owners')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Owner
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      queryClient.invalidateQueries({ queryKey: ['owners', variables.id] })
    },
  })
}

// =====================================================
// Delete owner mutation (soft delete)
// =====================================================

export function useDeleteOwner() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ownerId: string) => {
      // Soft delete
      const { error } = await supabase
        .from('owners')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', ownerId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
    },
  })
}

// =====================================================
// Add ownership (link owner to unit)
// =====================================================

export function useAddOwnership() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateOwnershipPayload) => {
      const { data, error } = await supabase
        .from('ownerships')
        .insert({
          owner_id: payload.ownerId,
          unit_id: payload.unitId,
          start_date: payload.startDate,
          share_numerator: payload.shareNumerator || 1,
          share_denominator: payload.shareDenominator || 1,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      queryClient.invalidateQueries({ queryKey: ['owners', variables.ownerId] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
  })
}

// =====================================================
// Remove ownership (unlink owner from unit)
// =====================================================

export function useRemoveOwnership() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: RemoveOwnershipPayload) => {
      if (payload.reason === 'ERROR') {
        // Permanent deletion for data entry error
        const { error } = await supabase
          .from('ownerships')
          .delete()
          .eq('id', payload.ownershipId)

        if (error) throw error
      } else {
        // Archive with end date for sale/transfer
        const { error } = await supabase
          .from('ownerships')
          .update({ end_date: payload.endDate })
          .eq('id', payload.ownershipId)

        if (error) throw error
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      queryClient.invalidateQueries({ queryKey: ['owners', variables.ownerId] })
      queryClient.invalidateQueries({ queryKey: ['units'] })
    },
  })
}
